import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PiggyBank, Fingerprint } from "lucide-react";
import { toast } from "sonner";
import { isWebAuthnSupported, loginWithBiometric } from "@/lib/webauthn";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [bioLoading, setBioLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Connexion réussie");
      navigate("/");
    } else {
      toast.error(res.error || "Échec de la connexion");
    }
  };

  const onBiometric = async () => {
    if (!isWebAuthnSupported()) {
      toast.error("Biométrie non supportée sur ce navigateur");
      return;
    }
    setBioLoading(true);
    try {
      await loginWithBiometric(email);
      toast.success("Connexion biométrique réussie");
      window.location.href = "/";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la biométrie");
    } finally {
      setBioLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FAF9F6]">
      <div className="flex flex-col justify-center px-6 sm:px-10 lg:px-20 py-12">
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-10 h-10 bg-stone-900 rounded-xl flex items-center justify-center">
              <PiggyBank className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-xl font-bold">Budgeo</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mb-3 tracking-tight">Bon retour.</h1>
          <p className="text-stone-500 mb-8">Connectez-vous pour suivre votre budget en toute simplicité.</p>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     required data-testid="login-email-input" className="rounded-xl h-11" placeholder="vous@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     required data-testid="login-password-input" className="rounded-xl h-11" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={loading}
                    className="w-full h-11 rounded-xl bg-stone-900 hover:bg-stone-800"
                    data-testid="login-submit-button">
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-stone-200" />
            <span className="text-xs text-stone-400 uppercase tracking-wider">ou</span>
            <div className="flex-1 h-px bg-stone-200" />
          </div>

          <Button variant="outline" type="button" onClick={onBiometric} disabled={bioLoading}
                  className="w-full h-11 rounded-xl border-stone-300"
                  data-testid="login-biometric-button">
            <Fingerprint className="w-5 h-5 mr-2" />
            {bioLoading ? "Vérification..." : "Se connecter avec biométrie"}
          </Button>

          <p className="text-sm text-stone-500 mt-6 text-center">
            Pas encore de compte ?{" "}
            <Link to="/register" className="text-stone-900 font-medium underline underline-offset-4" data-testid="link-register">
              Créer un compte
            </Link>
          </p>
        </div>
      </div>
      <div className="hidden md:block relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1758600432874-04ce2aa2d2e4?crop=entropy&cs=srgb&fm=jpg&q=85"
             alt="Happy user" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-stone-900/60 via-stone-900/20 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="font-display text-3xl font-semibold leading-tight">
            Votre argent, <br />enfin sous contrôle.
          </div>
          <p className="mt-3 text-white/80 max-w-sm">
            Suivez vos revenus et dépenses, fixez des budgets et laissez l'IA vous guider.
          </p>
        </div>
      </div>
    </div>
  );
}
